import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { addErrorNotification, getMemberJourneyGroups, logger, memberHasMoreJourneyResults, MemberJourneyListType, NotificationType } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useState } from 'react';
import { useOccConfig } from '../../context/occ/hooks/use-occ-config';
import { Button } from '../../subcomponents/button/button';
import { ButtonColor } from '../../subcomponents/button/types/color';
import { ButtonSize } from '../../subcomponents/button/types/size';
import { getIdForComponentNotification } from '../../utils/get-id-for-component-notification';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { MemberJourneyListGroup } from './subcomponents/group';
import { MemberJourneyListMessage, MemberJourneyListMessageType } from './subcomponents/message';
import { MemberJourneyListSkeleton } from './subcomponents/skeleton';
import { mapMemberJourneyListContext, MemberJourneyListContextInstance } from './types/context';
import { defaultMemberJourneyListProps, memberJourneyListPropsValidator } from './types/props';
import { loadJourneyListByType } from './utils/load-journey-list-by-type';
const b = bemClassBuilder('v-member-journey-list');
export const MemberJourneyList = observer(props => {
    const { enableSkeletonLoadingIndicators, locale: { date: dateConfig } } = useOccConfig();
    const context = mapMemberJourneyListContext(props);
    const { displayText, events, config } = props;
    const { listType, numberOfJourneysToLoad, showMessageForEmptyList } = config;
    const [loadingJourneyGroups, setLoadingJourneyGroups] = useState(false);
    const [loadingMoreJourneyGroups, setLoadingMoreJourneyGroups] = useState(false);
    const [loadError, setLoadError] = useState(false);
    useEffect(() => {
        (async () => {
            setLoadingJourneyGroups(true);
            setLoadError(false);
            try {
                await loadJourneyListByType({ type: listType, limit: numberOfJourneysToLoad, initialLoad: true }, dateConfig.startWeekOnSunday);
            }
            catch (error) {
                logger.error(error);
                setLoadError(true);
            }
            setLoadingJourneyGroups(false);
        })();
    }, [listType, numberOfJourneysToLoad, dateConfig]);
    const handleLoadButtonClicked = async () => {
        setLoadingMoreJourneyGroups(true);
        try {
            await loadJourneyListByType({ type: listType, limit: numberOfJourneysToLoad, initialLoad: false }, dateConfig.startWeekOnSunday);
        }
        catch (error) {
            logger.error(error);
            addErrorNotification({
                id: getIdForComponentNotification('member-journey-list', NotificationType.Error, 'failed-to-load-more-journeys'),
                messages: [displayText.failedToLoadMoreJourneysMessage]
            }, error);
        }
        setLoadingMoreJourneyGroups(false);
    };
    const handleNoJourneysButtonClicked = useCallback(() => {
        var _a;
        (_a = events.onNoJourneysActionButtonClicked) === null || _a === void 0 ? void 0 : _a.call(events);
    }, [events]);
    const journeyGroups = getMemberJourneyGroups(listType);
    const hasMoreResults = memberHasMoreJourneyResults(listType);
    // Loader
    if (!loadError && loadingJourneyGroups) {
        return enableSkeletonLoadingIndicators ? (_jsx(MemberJourneyListSkeleton, { filmMediaType: config.filmMediaType })) : null;
    }
    // Handle Empty Journey List
    if (!loadError && !journeyGroups.length) {
        if (!showMessageForEmptyList)
            return null;
        let messageType;
        switch (listType) {
            case MemberJourneyListType.Active:
            case MemberJourneyListType.NextJourney:
                messageType = MemberJourneyListMessageType.NoUpcomingJourneys;
                break;
            case MemberJourneyListType.Recent:
                messageType = MemberJourneyListMessageType.NoRecentJourneys;
                break;
            default:
                return null;
        }
        return (_jsx(MemberJourneyListContextInstance.Provider, Object.assign({ value: context }, { children: _jsx(MemberJourneyListMessage, Object.assign({ messageType: messageType }, { children: _jsx(Button, Object.assign({ onClick: handleNoJourneysButtonClicked, color: ButtonColor.Primary, size: ButtonSize.Medium }, { children: displayText.noJourneysActionButtonLabel })) })) })));
    }
    // Handle Load Error
    if (loadError) {
        return (_jsx(MemberJourneyListContextInstance.Provider, Object.assign({ value: context }, { children: _jsx(MemberJourneyListMessage, { messageType: MemberJourneyListMessageType.FailedToLoadData }) })));
    }
    return (_jsx(MemberJourneyListContextInstance.Provider, Object.assign({ value: context }, { children: _jsxs("div", Object.assign({ className: b.block() }, { children: [journeyGroups.map(journeyGroup => (_jsx(MemberJourneyListGroup, { journeyGroup: journeyGroup }, journeyGroup.id))), hasMoreResults && (_jsx(Button, { className: b.element('load-more-button'), color: ButtonColor.Secondary, size: ButtonSize.Small, loading: loadingMoreJourneyGroups, label: props.displayText.loadMoreButtonLabel, onClick: handleLoadButtonClicked }))] })) })));
});
MemberJourneyList.displayName = 'MemberJourneyList';
/**
 * The **Member Journey List** component renders a loyalty member’s next, active or recent journeys.
 */
export const MemberJourneyListComponent = withComponentWrapper(MemberJourneyList, defaultMemberJourneyListProps, memberJourneyListPropsValidator);
